<script setup lang="ts">
/**
 * "Add premade" modal for the RAW premade/custom highlights redesign.
 *
 * Styled to the Figma RAW redesign (node 1791:100362): a NovaModal with a centered header
 * ("Premade highlights" + close), a search bar with an "Expand all / Collapse all" toggle,
 * a list of accordion groups (each header shows "Group name (N selected)"), and a footer
 * with "Clear selection (N)" on the left and "Add (N)" on the right.
 *
 * ## Grouping (mirrors the old GroupedPremadeModal)
 * Premade options are masterdata items that each carry a `hierarchical_group_code`; the group
 * display name lives in a separate `hierarchicalGroups` map (code -> { name, archived }). We
 * reuse the proven `mapHighlightsToGroups` helper to bucket options by that code (invalid /
 * missing codes fall into UNCATEGORIZED) and to apply its sort (non-archived first,
 * uncategorized last, then alphabetical). Search reuses `usePremadesFiltering`, which matches
 * on either the item name or the group name — and we pass it this modal's own
 * `hierarchicalGroups` prop so group-name search resolves against the SAME map used for
 * grouping and labels. This keeps the redesigned modal consistent with the existing RAW
 * behaviour rather than reinventing grouping.
 *
 * Selection identity is the masterdata `code` (what `usePremadeList` dedupes on). The modal
 * lists ALL options and pre-checks the ones already added (`addedCodes`); it is the source of
 * truth for the premade selection, so checking adds and unchecking removes.
 *
 * The modal keeps a LOCAL working selection while open (seeded from `addedCodes`) and emits the
 * complete desired selection on Add — the parent reconciles (adds new, removes deselected). Per
 * the acceptance criteria there is NO "Select all" affordance on the group headers; the count
 * is shown (a) per accordion, (b) in the Clear-selection button, (c) in the Add button.
 */
import { computed, ref, watch } from "vue";
import NovaModal from "@/ui-kit/NovaModal/NovaModal.vue";
import NovaButton from "@/ui-kit/NovaButton/NovaButton.vue";
import NovaButtonIcon from "@/ui-kit/NovaButtonIcon/NovaButtonIcon.vue";
import NovaCheckbox from "@/ui-kit/NovaCheckbox/NovaCheckbox.vue";
import NovaCollapse from "@/ui-kit/NovaCollapse/NovaCollapse.vue";
import NovaInputText from "@/ui-kit/NovaInputText/NovaInputText.vue";
import NovaIcon from "@/ui-kit/NovaIcon/NovaIcon.vue";
import type { ExperiencePremadeItem } from "@/composables/useExperienceMasterDataApi";
import type { HierarchicalGroup } from "@/types/generated/ExperienceMasterDataApi";
import {
  mapHighlightsToGroups,
  UNCATEGORIZED_HIERARCHICAL_GROUP_KEY,
  UNCATEGORIZED_HIERARCHICAL_GROUP_NAME,
} from "@/features/experience-highlights/lib/map-highlights-to-groups";
import { usePremadesFiltering } from "@/features/masterdata-management/shared/composables/usePremadesFiltering";
import type { PremadeOption } from "@/features/experience-shared/highlights-v2/composables/usePremadeList";

const props = withDefaults(
  defineProps<{
    show: boolean;
    /** All premade masterdata options (grouped by their hierarchical_group_code). */
    masterdataOptions?: ExperiencePremadeItem[];
    /** Group metadata: code -> { name, archived, ... }. Provides accordion titles + sort. */
    hierarchicalGroups?: Map<string, HierarchicalGroup>;
    /** Codes already added to the list — shown pre-checked; unchecking removes them on Add. */
    addedCodes?: Set<string>;
  }>(),
  {
    masterdataOptions: () => [],
    hierarchicalGroups: () => new Map(),
    addedCodes: () => new Set(),
  }
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "add", options: PremadeOption[]): void;
}>();

// --- Local working selection (committed only on Add) --------------------------------------
/** Selection identity is the masterdata `code`. Seeded from `addedCodes` when the modal opens. */
const selectedCodes = ref<Set<string>>(new Set());
const searchQuery = ref("");
const expandAll = ref(true);
/** Per-group open state, keyed by group code. */
const openGroups = ref<Record<string, boolean>>({});

// Reset the working state each time the modal opens — pre-checking the already-added codes so
// the modal reflects the current selection and unchecking one will remove it on Add.
watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      selectedCodes.value = new Set(props.addedCodes);
      searchQuery.value = "";
      expandAll.value = true;
      openGroups.value = {};
    }
  },
  { immediate: true }
);

/** All options (dedupe by code), archived included — already-added ones are shown pre-checked. */
const availableOptions = computed<ExperiencePremadeItem[]>(() => props.masterdataOptions);

// Archived options aren't selectable here, but stay in `availableOptions` so `confirmAdd` can
// still resolve an already-saved archived code instead of dropping it on save.
const selectableOptions = computed<ExperiencePremadeItem[]>(() =>
  availableOptions.value.filter((option) => !option.archived)
);

// Search — reuses the shared premades filter (matches item name OR group name). Pass the
// modal's own `hierarchicalGroups` prop so group-name search uses the SAME map that grouping
// and labels use, rather than the global masterdata store.
const { matchingItems } = usePremadesFiltering(selectableOptions, searchQuery, () => props.hierarchicalGroups);

/** Filtered options bucketed + sorted into hierarchical groups (code -> items). */
const groups = computed(() => {
  const grouped = mapHighlightsToGroups(matchingItems.value ?? [], props.hierarchicalGroups);
  return [...grouped.entries()]
    .filter(([, options]) => options.length > 0)
    .map(([code, options]) => ({ code, name: groupName(code), options }));
});

function groupName(code: string): string {
  if (code === UNCATEGORIZED_HIERARCHICAL_GROUP_KEY) {
    return UNCATEGORIZED_HIERARCHICAL_GROUP_NAME;
  }
  return props.hierarchicalGroups.get(code)?.name ?? UNCATEGORIZED_HIERARCHICAL_GROUP_NAME;
}

const totalSelected = computed(() => selectedCodes.value.size);

/**
 * Whether the working selection differs from what is already added — i.e. there is something to
 * commit. Enables Add for both additions and removals (unchecking a pre-checked item), so a
 * user can also confirm "remove everything" even though nothing is selected.
 */
const hasChanges = computed(() => {
  const added = props.addedCodes;
  if (added.size !== selectedCodes.value.size) {
    return true;
  }
  for (const code of selectedCodes.value) {
    if (!added.has(code)) {
      return true;
    }
  }
  return false;
});

function selectedInGroup(options: ExperiencePremadeItem[]): number {
  return options.reduce((count, option) => count + (selectedCodes.value.has(option.code) ? 1 : 0), 0);
}

/** True while the user is actively searching — an empty/whitespace query does not count. */
const hasActiveSearch = computed(() => searchQuery.value.trim().length > 0);

/**
 * A group is open when its explicit state says so, else it follows the global expand toggle.
 * While a search is active every (surviving, non-empty) group is force-opened so a match can
 * never hide inside a collapsed accordion; the prior explicit/global state is left untouched
 * and takes over again once the query is cleared.
 */
function isGroupOpen(code: string): boolean {
  if (hasActiveSearch.value) {
    return true;
  }
  return openGroups.value[code] ?? expandAll.value;
}

function setGroupOpen(code: string, open: boolean): void {
  openGroups.value = { ...openGroups.value, [code]: open };
}

function toggleExpandAll(): void {
  expandAll.value = !expandAll.value;
  // Reset per-group overrides so every group follows the new global state.
  openGroups.value = {};
}

function toggleOption(code: string): void {
  const next = new Set(selectedCodes.value);
  if (next.has(code)) {
    next.delete(code);
  } else {
    next.add(code);
  }
  selectedCodes.value = next;
}

function clearSelection(): void {
  selectedCodes.value = new Set();
}

function close(): void {
  emit("close");
}

/**
 * Emits the COMPLETE desired premade selection (every currently-checked option, including the
 * already-added ones that were pre-checked). The parent reconciles: new codes are added,
 * previously-added codes that are no longer checked are removed.
 */
function confirmAdd(): void {
  const chosen = availableOptions.value.filter((option) => selectedCodes.value.has(option.code));
  emit(
    "add",
    chosen.map((option) => ({ code: option.code, name: option.name }))
  );
}
</script>

<template>
  <NovaModal :show="show" @click:on-overlay="close">
    <div class="flex w-[640px] flex-col" data-testid="add-premade-modal">
      <!-- Header -->
      <div class="relative flex flex-col items-center gap-4 px-4 pb-2 pt-4">
        <div class="flex min-h-[22px] w-full items-center justify-center">
          <h2 class="text-center font-bold text-[16px] leading-5 text-text-100">
            {{ $t("experience.highlights.v2.premade.modal.title") }}
          </h2>
          <span class="absolute right-3 top-3">
            <NovaButtonIcon
              name="close"
              theme="dark"
              shape="square"
              :size="16"
              :aria-label="$t('common.close')"
              data-testid="add-premade-modal-close"
              @click="close"
            />
          </span>
        </div>

        <!-- Search + expand all -->
        <div class="flex w-full items-center gap-2 rounded-lg border border-neutral-60 p-2">
          <div class="flex-1">
            <NovaInputText
              id="add-premade-search"
              v-model="searchQuery"
              size="sm"
              left-icon="search"
              :placeholder="$t('experience.highlights.v2.premade.modal.search_placeholder')"
            />
          </div>
          <NovaButton
            variant="action"
            size="xs"
            theme="primary"
            data-testid="add-premade-expand-all"
            @click="toggleExpandAll"
          >
            {{ expandAll ? $t("common.action.collapse_all") : $t("common.action.expand_all") }}
            <NovaIcon :name="expandAll ? 'chevron-up' : 'chevron-down'" :size="14" class="ml-1" />
          </NovaButton>
        </div>
      </div>

      <!-- Content: accordion groups -->
      <div class="flex max-h-[402px] flex-col gap-2 overflow-y-auto px-5 py-2">
        <p v-if="groups.length === 0" class="py-4 text-center text-[14px] text-text-90" data-testid="add-premade-empty">
          {{ $t("experience.highlights.v2.premade.modal.no_results") }}
        </p>

        <NovaCollapse
          v-for="group in groups"
          :key="group.code"
          size="md"
          :model-value="isGroupOpen(group.code)"
          data-testid="add-premade-group"
          @update:model-value="setGroupOpen(group.code, $event)"
        >
          <template #title>
            <span class="flex items-center gap-1">
              <span class="font-semibold text-[14px] text-text-100">{{ group.name }}</span>
              <span class="text-[14px] text-text-90" data-testid="add-premade-group-count">
                {{
                  $t("experience.highlights.v2.premade.modal.group_selected", {
                    placeholders: { count: selectedInGroup(group.options) },
                  })
                }}
              </span>
            </span>
          </template>

          <ul class="flex flex-col">
            <li v-for="option in group.options" :key="option.code" class="flex items-center px-4 py-2">
              <NovaCheckbox
                :value="option.code"
                :label="option.name"
                :status="selectedCodes.has(option.code) ? 'checked' : 'unchecked'"
                data-testid="add-premade-option"
                @update:status="toggleOption(option.code)"
              />
            </li>
          </ul>
        </NovaCollapse>
      </div>

      <!-- Footer -->
      <div class="flex h-16 items-center justify-between border-t border-neutral-60 px-5 py-4">
        <NovaButton
          variant="action"
          size="sm"
          theme="primary"
          :disabled="totalSelected === 0"
          data-testid="add-premade-clear"
          @click="clearSelection"
        >
          <NovaIcon name="arrow-rotate-left" :size="16" class="mr-2" />
          {{ $t("experience.highlights.v2.premade.modal.clear_selection", { placeholders: { count: totalSelected } }) }}
        </NovaButton>
        <NovaButton
          variant="contained"
          size="sm"
          :disabled="!hasChanges"
          data-testid="add-premade-add"
          @click="confirmAdd"
        >
          {{ $t("experience.highlights.v2.premade.modal.add", { placeholders: { count: totalSelected } }) }}
        </NovaButton>
      </div>
    </div>
  </NovaModal>
</template>
